/**
 * SameNameWarning — Wave 3 · Feb 2026.
 *
 * Subtle disambiguation strip surfaced on university detail pages
 * whose base name collides with another accredited institution
 * (e.g. Trinity College Hartford CT vs Trinity University TX).
 *
 * Data source: `uni.same_name_warnings` — populated server-side in
 * `routes/unis_core.py::get_uni()` by looking up the uni against
 * `data/known_different_institutions.py`. Silent for the ~4800
 * unis without a registered sister.
 *
 * Design intent:
 *  - Non-scary. This is NOT an error. Two schools legitimately
 *    share a brand keyword. We're helping the student avoid a
 *    routine mix-up, not warning them off.
 *  - No auto-close. It's a subtle stripe, not a modal.
 *  - Sister links deep-link straight to the other uni's page so
 *    the student can eyeball the difference in one click.
 */
import { Link } from "react-router-dom";
import { Info } from "lucide-react";

export default function SameNameWarning({ uni }) {
  const w = uni?.same_name_warnings;
  if (!w || !w.sisters || w.sisters.length === 0) return null;

  const isPlural = w.sisters.length > 1;

  return (
    <div
      data-testid="same-name-warning"
      className="mt-3 rounded-md border border-sky-300 bg-sky-50/70 px-3 py-2 text-[12px] text-stone-700"
    >
      <div className="flex items-start gap-1.5">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-sky-700" aria-hidden />
        <div>
          <span className="font-medium text-sky-900">
            Not to be confused with{" "}
            {w.sisters.map((s, i) => (
              <span key={s.id}>
                <Link
                  to={`/uni/${s.id}`}
                  data-testid={`same-name-sister-${s.id}`}
                  className="underline decoration-sky-400 hover:decoration-sky-700"
                >
                  {s.name}
                </Link>
                {s.city && (
                  <span className="text-stone-500 font-normal">
                    {" "}
                    ({s.city})
                  </span>
                )}
                {i < w.sisters.length - 1 && (
                  <span>{i === w.sisters.length - 2 ? " or " : ", "}</span>
                )}
              </span>
            ))}
            {isPlural ? " — separate institutions" : " — a separate institution"}
            .
          </span>
          {w.note && (
            <div className="mt-0.5 text-[11px] text-stone-500 leading-snug">
              {w.note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
